import { useCallback } from 'react';
import UIDuck from '../redux/commonDucks/ui.duck';
import Cookies from 'js-cookie';
import {
    MOP_ID_COOKIE_NAME,
    MOP_CAR_CONFIGURATION_COOKIE_NAME,
    MOP_JOURNEY_COOKIE_NAME,
    SESSION_DOMAIN,
    IS_DEV,
} from '../constants/main';
import routes from '../constants/routes';
import sessionInfoApi from '../services/core/seassionInfoApi.service';
import { getSessionIdCookie, setSessionIdCookie } from '@utils/Session.utils';
import { IConfigurableCar, IDeal } from '../interfaces/Car';
import { IPxValuation } from '../partExchange/interfaces/PxWidget';
import { useDispatch, useSelector } from 'react-redux';
import carDetailsDuck from '../redux/carDetails/carDetails.duck';
import FilterDuck from '../redux/filters/filter.duck';
import ConfiguratorDuck from '../redux/configurator/configurator.duck';
import { TBudgetType } from '../interfaces/IFilters';
import useCustomRouter from './useCustomRouter';
import userDuck from '../redux/user/user.duck';
import { BASKET_OFFERS_MODAL_QUERY } from '../pages/offers/login';
import { BRANDID_OFFERS_RETURN_URL } from '../constants/myAccount';
import { ICarDetail } from '../redux/carDetails/carDetails.duck.interface';
import { DealService, CarJourneyType } from '../services';
import CarDetailsDuck from '../redux/carDetails/carDetails.duck';
import GlobalDuck from 'src/redux/global/global.duck';
import { Redux } from 'src/redux/redux.interface';
import {useGlobalDuck} from "@hooks/useGlobalDuck";

export interface ISelectedState {
    carDetails: IConfigurableCar;
    pxState?: IPxValuation;
    paymentJourney: TBudgetType;
    currentDeal?: IDeal;
    isLoggedIn?: boolean | null;
}

export const useOffersRedirect = () => {
    const router = useCustomRouter();
    const dispatch = useDispatch();

    const redirectTo = `${BRANDID_OFFERS_RETURN_URL}${routes.BASKET}?${BASKET_OFFERS_MODAL_QUERY}=true`;

    const { carDetails, pxState, paymentJourney, currentDeal, isLoggedIn }: ISelectedState = useSelector((state) => ({
        paymentJourney: FilterDuck.getPaymentJourneyType(state),
        carDetails: carDetailsDuck.getOwnState(state).currentCarDetails,
        pxState: ConfiguratorDuck.getOwnState(state).pxState,
        currentDeal: carDetailsDuck.getOwnState(state).currentDeal,
        isLoggedIn: userDuck.getOwnState(state).userAuth.isLoggedIn,
    }));
    const { carJourney } = useGlobalDuck();

    const redirectToOffers = useCallback(async () => {
        dispatch(UIDuck.setLoading(5));

        const configId = carDetails.stock
            ? (carDetails as ICarDetail).orders[0].uuid
            : carDetails._id ?? carDetails.externalId;

        const { headers, data } = await DealService.setConfiguration(
            getSessionIdCookie(),
            configId,
            paymentJourney,
            carDetails.stock ? CarJourneyType.STOCK : CarJourneyType.CONFIGURABLE
        );

        if (headers['x-auth-token']) {
            setSessionIdCookie(headers['x-auth-token']);
        }

        if (data?.success) {
            const { deal } = data.data ?? {};
            dispatch(CarDetailsDuck.setCurrentDealAndCarDetail(deal));
        }

        let mopId = currentDeal?.mopId;

        // if MOP not created or MOP created with different car configuration or MOP id on Deal not the same
        if (!mopId) {
            try {
                const { data } = await sessionInfoApi.createRetailMop(getSessionIdCookie());
                mopId = data.mopId;

                // save MOP ID and the car configuration
                Cookies.set(MOP_ID_COOKIE_NAME, mopId, { domain: SESSION_DOMAIN, sameSite: 'strict', secure: !IS_DEV });
                Cookies.set(MOP_CAR_CONFIGURATION_COOKIE_NAME, carDetails.externalId, {
                    domain: SESSION_DOMAIN,
                    sameSite: 'strict',
                    secure: !IS_DEV,
                });
                Cookies.set(MOP_JOURNEY_COOKIE_NAME, paymentJourney, {
                    domain: SESSION_DOMAIN,
                    sameSite: 'strict',
                    secure: !IS_DEV,
                });

                // TODO dispatch update currentDeal with new mopId
            } catch (err: any) {
                console.error(err);
                return;
            }
        }

        // we need to link saved PX to MOP
        if (pxState?.id) {
            await sessionInfoApi.linkPxToMop(getSessionIdCookie(), mopId, pxState.id);
        }

        dispatch(UIDuck.setLoading(-5));

        router.push(routes.OFFERS_DELIVERY);
    }, [
        carDetails?.externalId,
        pxState?.id,
        paymentJourney,
        currentDeal?.mopId,
        currentDeal?.dealerId,
        isLoggedIn,
        redirectTo,
        carJourney,
    ]);

    return { redirectToOffers };
};
